import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Trash2 } from 'lucide-react';
import { SourcesModal } from './SourcesModal';
import { DeleteMessageModal } from './DeleteMessageModal';
import type { Message, RagSource } from '../types/chat';

interface ChatInterfaceProps {
  messages: Message[];
  onDeleteMessage?: (messageId: string) => void | Promise<void>;
}

function formatMessageTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ChatMarkdown({
  content,
  variant,
}: {
  content: string;
  variant: 'user' | 'assistant';
}) {
  const proseUser =
    'prose prose-sm max-w-none prose-invert prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-pre:bg-slate-900 prose-pre:text-slate-100';
  const proseAssistant =
    'prose prose-sm max-w-none prose-slate prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 dark:prose-invert dark:prose-headings:text-slate-100';

  return (
    <div className={variant === 'user' ? proseUser : proseAssistant}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={
                variant === 'user'
                  ? 'text-sky-300 underline underline-offset-2 hover:text-sky-200'
                  : 'text-slate-700 underline underline-offset-2 hover:text-slate-900 dark:text-sky-400 dark:hover:text-sky-300'
              }
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export function ChatInterface({ messages, onDeleteMessage }: ChatInterfaceProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sourcesModalPayload, setSourcesModalPayload] = useState<RagSource[] | null>(null);
  const [messageIdPendingDelete, setMessageIdPendingDelete] = useState<string | null>(null);

  const chatWideSources = useMemo(() => {
    const byId = new Map<string, string>();
    for (const m of messages) {
      if (m.role !== 'assistant' || !m.rag_sources?.length) continue;
      for (const s of m.rag_sources) {
        byId.set(s.markdown_file_id, s.title);
      }
    }
    return [...byId.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([markdown_file_id, title]) => ({ markdown_file_id, title }));
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white dark:bg-slate-950">
      {chatWideSources.length > 0 && (
        <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-6 py-3 dark:border-slate-800 dark:bg-slate-900">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Documents referenced in this chat
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {chatWideSources.map((s) => (
              <li key={s.markdown_file_id}>
                <Link
                  to={`/documents/${s.markdown_file_id}`}
                  className="text-slate-700 hover:text-slate-900 hover:underline dark:text-slate-300 dark:hover:text-white"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="p-6 space-y-6">
        {messages.length === 0 ? (
          <div className="flex min-h-[min(60vh,32rem)] items-center justify-center text-slate-400 dark:text-slate-500">
            <div className="text-center">
              <p className="mb-2 text-lg dark:text-slate-300">Start a new conversation</p>
              <p className="text-sm dark:text-slate-400">Ask questions about your markdown documents</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`group relative max-w-3xl ${
                    message.role === 'assistant' || onDeleteMessage ? 'pb-9' : ''
                  }`}
                >
                  <div
                    className={`rounded-2xl px-6 py-4 ${
                      message.role === 'user'
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100'
                    }`}
                  >
                    <ChatMarkdown content={message.content} variant={message.role} />
                  </div>
                  {message.role === 'user' && onDeleteMessage && (
                    <div className="absolute bottom-0 left-0 flex max-w-[calc(100%-0.5rem)] flex-wrap items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => setMessageIdPendingDelete(message.id)}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-600 bg-slate-900/95 text-slate-300 shadow-sm hover:bg-red-950/90 hover:text-red-200 hover:border-red-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                        title="Delete message"
                        aria-label="Delete message"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <time
                        dateTime={message.created_at}
                        className="ml-1 shrink-0 border-l border-slate-600 pl-2 text-[11px] tabular-nums text-slate-400"
                        title={message.created_at}
                      >
                        {formatMessageTimestamp(message.created_at)}
                      </time>
                    </div>
                  )}
                  {message.role === 'assistant' && (
                    <div className="absolute bottom-0 left-0 flex max-w-[calc(100%-0.5rem)] flex-wrap items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      {onDeleteMessage && (
                        <button
                          type="button"
                          onClick={() => setMessageIdPendingDelete(message.id)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:border-slate-600 dark:bg-slate-900 dark:hover:border-red-900 dark:hover:bg-red-950/50"
                          title="Delete message"
                          aria-label="Delete message"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {message.rag_sources && message.rag_sources.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSourcesModalPayload(message.rag_sources ?? [])}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 dark:border-slate-600 dark:bg-slate-900 dark:hover:border-sky-800 dark:hover:bg-sky-950/50 dark:hover:text-sky-400"
                          title={`View parent documents (${message.rag_sources.length})`}
                          aria-label={`View ${message.rag_sources.length} source document${message.rag_sources.length === 1 ? '' : 's'} for this reply`}
                        >
                          <FileText className="h-4 w-4" />
                        </button>
                      )}
                      <time
                        dateTime={message.created_at}
                        className="ml-1 shrink-0 border-l border-slate-200 pl-2 text-[11px] tabular-nums text-slate-500 dark:border-slate-600 dark:text-slate-400"
                        title={message.created_at}
                      >
                        {formatMessageTimestamp(message.created_at)}
                      </time>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {sourcesModalPayload && sourcesModalPayload.length > 0 && (
        <SourcesModal
          sources={sourcesModalPayload}
          onClose={() => setSourcesModalPayload(null)}
        />
      )}

      {messageIdPendingDelete && onDeleteMessage && (
        <DeleteMessageModal
          onClose={() => setMessageIdPendingDelete(null)}
          onDelete={() => void onDeleteMessage(messageIdPendingDelete)}
        />
      )}
    </div>
  );
}
